import type { Session, User } from '@supabase/supabase-js';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { captureWithOfflineSupport } from '../services/posthog';

export function usePostHogIdentify(
  session: Session | null, 
  user: User | null
) {
  const posthog = usePostHog();
  const hasIdentified = useRef(false);
  
  useEffect(() => {
    if (!posthog) return;
    
    if (session && user && !hasIdentified.current) {
      if (import.meta.env.VITE_POSTHOG_DEBUG || (typeof window !== 'undefined' && (location.hostname === 'localhost' || location.hostname === '127.0.0.1'))) {
        console.log('[PostHog] Identifying user:', user.id);
      }
      // Identify the user
      posthog.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.full_name || user.user_metadata?.name,
        avatar_url: user.user_metadata?.avatar_url,
        created_at: user.created_at,
        auth_provider: session.user.app_metadata?.provider || 'email',
        last_sign_in: user.last_sign_in_at
      });
      
      // Track login event
      captureWithOfflineSupport('user_logged_in', {
        method: session.user.app_metadata?.provider || 'email',
        is_new_user: !user.last_sign_in_at
      });
      
      hasIdentified.current = true;
    }
  }, [session, user, posthog]);
  
  // Reset on logout
  useEffect(() => {
    if (!posthog) return;
    
    if (!session && !user && hasIdentified.current) {
      posthog.reset();
      hasIdentified.current = false;
    }
  }, [session, user, posthog]);
}
